import { StateCreator } from 'zustand';
import { LocalStorageRepository, localStorageRepositorySingleton } from '@warehouse/shared/util';
import { quickbaseAuthServiceSingleton } from './quickbase-auth';
import { CACHE_DURATION, CACHE_KEY, QuickbaseAuthFlow } from './quickbase-types';

export interface QuickbaseCommonSlice {
	isLoading: boolean;
	/** legacy only: the verified flow trusts the frame probe, never a stored flag. */
	isAuthenticated: boolean;
	authFlow: QuickbaseAuthFlow;
	error: string | null;
	/** Deep link to the Quickbase page. */
	embedUrl: string | null;
	/** What the iframe actually loads: the probe URL in the verified flow, otherwise `embedUrl`. */
	frameUrl: string | null;
	showAuthOverlay: boolean;
	actions: {
		authenticate(): Promise<void>;
		// Frame probe found no session: redirect through SSO when the cooldown allows (resolves true, page unloads), else false so the UI can fall back.
		recoverSession(): Promise<boolean>;
		setShowAuthOverlay(show: boolean): void;
		dismissAuthOverlay(): void;
	};
}

function readCachedAuth(storage: LocalStorageRepository): boolean {
	const cachedAuth = storage.get(CACHE_KEY);
	if (!cachedAuth) return false;

	try {
		const { timestamp, value } = JSON.parse(cachedAuth);
		const isExpired = Date.now() - timestamp > CACHE_DURATION;
		return !isExpired && value === true;
	} catch {
		console.warn('Invalid cache format, ignoring it');
		return false;
	}
}

function writeCachedAuth(storage: LocalStorageRepository): void {
	storage.set(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), value: true }));
}

export function resolveFrameUrl(embedUrl: string): string {
	return quickbaseAuthServiceSingleton.get().buildEmbedProbeUrl(embedUrl) ?? embedUrl;
}

export const createCommonQuickbaseSlice: StateCreator<QuickbaseCommonSlice> = (set) => {
	const storageRepository = localStorageRepositorySingleton.get();
	const authService = quickbaseAuthServiceSingleton.get();
	const authFlow = authService.getAuthFlow();
	const isLegacy = authFlow === 'legacy';

	const actions: QuickbaseCommonSlice['actions'] = {
		async authenticate() {
			set({ isLoading: true, error: null, showAuthOverlay: false });

			try {
				set({
					isLoading: true,
					isAuthenticated: false,
					error: null,
					showAuthOverlay: true,
				});

				const success = await authService.authenticate();

				if (!success) {
					set({
						isLoading: false,
						isAuthenticated: false,
						error: 'Auth failed',
						showAuthOverlay: true,
					});
					return;
				}

				if (isLegacy) writeCachedAuth(storageRepository);

				set({
					isLoading: false,
					isAuthenticated: true,
					error: null,
					showAuthOverlay: false,
				});
			} catch (error) {
				set({
					isLoading: false,
					isAuthenticated: false,
					error: error instanceof Error ? error.message : 'Authentication failed',
					showAuthOverlay: true,
				});
			}
		},

		async recoverSession() {
			if (!authService.canAutoRecover()) {
				return false;
			}

			await actions.authenticate();
			return true;
		},

		setShowAuthOverlay(show: boolean) {
			set({ showAuthOverlay: show });
		},

		dismissAuthOverlay() {
			if (isLegacy) writeCachedAuth(storageRepository);

			set({
				showAuthOverlay: false,
			});
		},
	};

	return {
		isLoading: false,
		isAuthenticated: isLegacy && readCachedAuth(storageRepository),
		authFlow,
		error: null,
		embedUrl: null,
		frameUrl: null,
		showAuthOverlay: false,
		actions,
	};
};
